import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { PenLine, AudioLines, MailOpen, ArrowRight } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Reveal, MaskedLines } from "../components/Reveal";
import { Divider } from "../components/Divider";
import { PillButton } from "../components/PillButton";
import { IMAGES, startCheckout } from "../lib/constants";

const STEPS = [
  {
    n: "01",
    icon: PenLine,
    title: "Share your intentions",
    body: "After payment, a short form will appear right here on this page. Take a quiet moment to reflect and describe what you would like to manifest in this chapter of your life—whether it relates to abundance, career, personal growth, confidence, love, or another meaningful intention. Write freely and in your own words. Your mind holds infinite possibilities and your intention is where the journey begins.",
  },
  {
    n: "02",
    icon: AudioLines,
    title: "We create your audio",
    body: "We write a script based on your words, record the affirmations, and blend them with a gentle soundscape. Your audio is crafted with care and intention.",
  },
  {
    n: "03",
    icon: MailOpen,
    title: "Delivered to your inbox within 48h",
    body: "You'll receive an email with your personalized MP3, ready to download and keep forever. No subscriptions, no expiry.",
  },
];

const HeroImage = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "12%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.08, 1.16]);

  return (
    <div ref={ref} className="mx-auto w-[88%] max-w-5xl overflow-hidden rounded-[28px] sm:w-4/5">
      <motion.img
        style={{ y, scale }}
        src={IMAGES.howItWorksHero}
        alt="A hand writing affirmations on paper with a golden soundwave glowing above the desk"
        className="h-full w-full object-cover"
      />
    </div>
  );
};

const HowItWorks = () => {
  return (
    <div className="bg-cream">
      <Navbar />
      <main className="pt-36">
        <section className="px-6 lg:px-12" data-testid="hiw-header">
          <div className="mx-auto max-w-4xl text-center">
            <p className="text-xs font-medium uppercase tracking-[0.24em] text-terracotta">
              How it works
            </p>
            <h1 className="mx-auto mt-6 max-w-3xl font-serif text-4xl font-medium leading-[1.05] text-ink sm:text-5xl lg:text-6xl">
              <MaskedLines lines={["From your intentions", "to your personalized audio."]} />
            </h1>
            <p className="mx-auto mt-7 max-w-2xl text-base leading-relaxed text-ink-soft sm:text-lg">
              Three simple steps between your payment and your first listen. No apps, accounts or
              subscriptions required.
            </p>
          </div>
        </section>

        <section className="px-6 py-16 lg:px-12" data-testid="hiw-hero-image">
          <Reveal>
            <HeroImage />
            <p className="mx-auto mt-8 max-w-xl text-center font-serif text-xl italic text-ink-soft sm:text-2xl">
              Your words. Your intentions. Your personal sound experience.
            </p>
            <Divider className="mt-8" />
          </Reveal>
        </section>

        <section className="px-6 py-16 lg:px-12" data-testid="hiw-steps">
          <div className="mx-auto grid max-w-6xl gap-y-14 md:grid-cols-2 md:gap-x-12 lg:grid-cols-3 lg:gap-x-0">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.12}>
                <div
                  className={`px-0 lg:px-10 ${
                    i > 0
                      ? "border-t border-[rgba(43,38,32,0.12)] pt-14 md:border-t-0 md:pt-0 lg:border-l"
                      : ""
                  }`}
                  data-testid={`hiw-step-${i}`}
                >
                  <span className="font-serif text-5xl font-medium text-terracotta/50">{s.n}</span>
                  <span className="mt-6 flex h-12 w-12 items-center justify-center rounded-full border border-terracotta/30 text-terracotta">
                    <s.icon strokeWidth={1.4} size={22} />
                  </span>
                  <h3 className="mt-6 font-serif text-2xl font-medium leading-tight text-ink">
                    {s.title}
                  </h3>
                  <p className="mt-4 text-base leading-relaxed text-ink-soft">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        <section className="px-6 py-24 text-center lg:px-12" data-testid="hiw-cta">
          <Reveal>
            <PillButton
              variant="terracotta"
              className="text-base"
              onClick={startCheckout}
              data-testid="hiw-cta-button"
            >
              Create my audio — €14,90
              <ArrowRight size={18} strokeWidth={1.75} className="transition-transform duration-300 group-hover:translate-x-1" />
            </PillButton>
          </Reveal>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default HowItWorks;
